import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";
import { render } from "react-dom";

import {
	NetworkErrorBoundary,
	SubscriptionManager,
	PollingSubscription,
	DevToolsManager
} from "rest-hooks";
import { Manager, CacheProvider, NetworkManager } from "@rest-hooks/core";

import { App } from "./App";
import * as serviceWorker from "./serviceWorker";
import CssBaseline from "@material-ui/core/CssBaseline";
import { HiddenInProductionTenantSelector } from "@ploy-ui/tenants";

import iframeResizerContentWindow from "iframe-resizer/js/iframeResizer.contentWindow";

import { EnvProvider } from "@ploy-lib/core";
import { EventProvider, createEventManager } from "@ploy-lib/calculation";
import {
	DefaultLinearProgress,
	BrandTenantProvider,
	ErrorPage,
	GlobalSuspenseProvider
} from "@ploy-ui/core";
import { createTheme, ThemeProvider } from "@material-ui/core";
import { bootstrapEventProviderForSnippetMessaging } from "./bootstrap-event-provider";
import { LayoutWithBanner } from "./LayoutWithBanner";
import { AuthenticateQueryAgent, getAgentParams } from "./AuthenticatedAgent";
import qs from "qs";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { FirstPartyAccessPage } from "./FirstPartyAccessPage";

import "@fortawesome/fontawesome-free/css/all.css";
import "@fortawesome/fontawesome-free/css/v4-shims.css";
import { CookiesGateKeeper } from "./CookiesGateKeeper";

const element = document.getElementById("root");

const baseTheme = (inIframe: boolean) =>
	createTheme(
		inIframe
			? {
					palette: {
						background: {
							default: "rgba(0,0,0,0)"
						}
					}
			  }
			: undefined
	);

function inIframe() {
	try {
		return window.self !== window.top;
	} catch (e: any) {
		return true;
	}
}

const managers: Manager[] = [
	new NetworkManager(15 * 60 * 1000),
	new SubscriptionManager(PollingSubscription)
];

if (process.env.NODE_ENV !== "production")
	managers.unshift(new DevToolsManager());

export function IndexComponent(props: any) {
	const queryString = window.location.search;
	const qsParams = qs.parse(queryString, {
		ignoreQueryPrefix: true,
		allowDots: true
	});
	const isInIframe = inIframe();
	const eventProvider = createEventManager<string, string | number | boolean>();
	const { design } = qsParams as { design?: string };

	const basepath = process.env.PUBLIC_URL;

	// TODO: extract allowed origin from user context
	bootstrapEventProviderForSnippetMessaging(eventProvider, "*");
	return (
		<Router basename={basepath}>
			<ThemeProvider theme={baseTheme(isInIframe)}>
				<EnvProvider envFilter={e => e.includes("forms")}>
					<CacheProvider managers={managers}>
						<EventProvider value={eventProvider}>
							<GlobalSuspenseProvider
								fallback={
									<>
										<DefaultLinearProgress />
										<CssBaseline />
										<HiddenInProductionTenantSelector />
									</>
								}
							>
								<NetworkErrorBoundary
									fallbackComponent={props => (
										<BrandTenantProvider
											defaultLocale="nb-NO"
											defaultCurrency="NOK"
											theme={{
												paletteBackgroundDefault: isInIframe
													? "rgba(0,0,0,0)"
													: undefined,
												design: design
											}}
										>
											<ErrorPage {...props} />
											<CssBaseline />
											<HiddenInProductionTenantSelector />
										</BrandTenantProvider>
									)}
								>
									<BrandTenantProvider
										defaultLocale="nb-NO"
										defaultCurrency="NOK"
										theme={{
											paletteBackgroundDefault: isInIframe
												? "rgba(0,0,0,0)"
												: undefined,
											design: design
										}}
									>
										<LayoutWithBanner disableBanner={isInIframe}>
											<Routes>
												<Route
													path="/firstpartyaccess"
													element={<FirstPartyAccessPage />}
												/>
												<Route path="*" element={<AppWrapper />} />
											</Routes>
										</LayoutWithBanner>
										<HiddenInProductionTenantSelector />
										<CssBaseline />
									</BrandTenantProvider>
								</NetworkErrorBoundary>
							</GlobalSuspenseProvider>
						</EventProvider>
					</CacheProvider>
				</EnvProvider>
			</ThemeProvider>
		</Router>
	);
}

const AppWrapper = (props: any) => {
	const { isCookieless, token } = getAgentParams();
	if (!isCookieless || token)
		return (
			<CookiesGateKeeper>
				<AuthenticateQueryAgent>
					<App />
				</AuthenticateQueryAgent>
			</CookiesGateKeeper>
		);
	return (
		<AuthenticateQueryAgent>
			<App />
		</AuthenticateQueryAgent>
	);
};

function renderApp() {
	render(<IndexComponent />, element);
}

// This needs to be referenced so it isn't tree-shaken.
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
iframeResizerContentWindow;

renderApp();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();

if (process.env.NODE_ENV === "development") {
	// Hot reload reducers (requires Webpack or Browserify HMR to be enabled)
	if (module.hot) {
		module.hot.accept("./App", () => renderApp());
	}
	if (module.hot) {
		module.hot.accept("@ploy-ui/tenants", () => renderApp());
	}
}
